import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { SUPPORT_ARTICLES } from '../SupportCenter';

// Per-route title / description / canonical / robots for public pages (SEO-WEB-A).
const SITE_URL = 'https://onlymass.net';
const HOME_TITLE = 'OnlyMass — Fitness Creator Monetization Platform';
const HOME_DESC = 'OnlyMass helps fitness creators monetize their expertise with workout plans, meal plans, subscriptions, premium content, tips and challenges.';

const PUBLIC_PAGES = {
  '/': { title: HOME_TITLE, description: HOME_DESC },
  '/signup': { title: 'Sign Up — OnlyMass', description: 'Create your free OnlyMass account to follow fitness creators or start monetizing your own workout plans, meal plans and premium content.', canonical: '/' },
  '/about': { title: 'About OnlyMass — Fitness Creator Monetization Platform', description: 'OnlyMass is a creator-first fitness platform where fitness creators build their business and fans get real results.' },
  '/careers': { title: 'Careers at OnlyMass', description: 'Help us build the future of fitness creator monetization. Explore careers at OnlyMass.' },
  '/support': { title: 'OnlyMass Support — Help Center', description: 'Get help with your OnlyMass account, subscriptions and purchases, creator payouts, and safety and reporting.' },
  '/terms': { title: 'Terms of Service — OnlyMass', description: 'The Terms of Service that govern your use of the OnlyMass website, platform and related services.' },
  '/privacy': { title: 'Privacy Policy — OnlyMass', description: 'How OnlyMass collects, uses and protects your personal information when you use our platform and services.' },
  '/community-guidelines': { title: 'Community Guidelines — OnlyMass', description: 'Standards for a safe, respectful fitness community on OnlyMass, for creators and subscribers alike.' },
  '/copyright': { title: 'Copyright & DMCA Policy — OnlyMass', description: 'How OnlyMass handles intellectual property and copyright infringement reports.' },
  '/cookies': { title: 'Cookie Policy — OnlyMass', description: 'How OnlyMass uses cookies and similar technologies on our platform.' },
};

const PRIVATE_PREFIXES = ['/dashboard', '/admin', '/auth-callback', '/payment-', '/subscription-', '/tip-', '/my-', '/purchase-history', '/connections'];

const clip = (text) => (text.length > 155 ? `${text.slice(0, 152).trimEnd()}…` : text);

const setMeta = (attr, key, content) => {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!content) { if (el) el.remove(); return; }
  if (!el) { el = document.createElement('meta'); el.setAttribute(attr, key); document.head.appendChild(el); }
  el.setAttribute('content', content);
};

const setCanonical = (href) => {
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!href) { if (el) el.remove(); return; }
  if (!el) { el = document.createElement('link'); el.setAttribute('rel', 'canonical'); document.head.appendChild(el); }
  el.setAttribute('href', href);
};

const resolvePage = (path) => {
  if (PUBLIC_PAGES[path]) return PUBLIC_PAGES[path];
  const slug = path.startsWith('/support/') ? path.slice('/support/'.length) : null;
  const article = slug && SUPPORT_ARTICLES[slug];
  if (!article) return null;
  const intro = article.content.find((b) => b.type === 'p');
  return { title: `${article.title} — OnlyMass Support`, description: clip(intro ? intro.text : article.title) };
};

const applyPublic = (path, page) => {
  const canonical = `${SITE_URL}${page.canonical || path}`;
  document.title = page.title;
  setMeta('name', 'description', page.description);
  setMeta('name', 'robots', 'index, follow');
  setCanonical(canonical);
  setMeta('property', 'og:title', page.title);
  setMeta('property', 'og:description', page.description);
  setMeta('property', 'og:url', canonical);
  setMeta('name', 'twitter:title', page.title);
  setMeta('name', 'twitter:description', page.description);
};

export const SeoManager = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    const path = pathname.replace(/\/+$/, '') || '/';
    if (path === '/invest') { setCanonical(`${SITE_URL}/invest`); return; } // InvestPage owns its own meta
    const page = resolvePage(path);
    if (page) { applyPublic(path, page); return; }
    document.title = 'OnlyMass';
    if (PRIVATE_PREFIXES.some((p) => path.startsWith(p))) {
      setMeta('name', 'robots', 'noindex, nofollow');
      setCanonical(null);
      return;
    }
    setMeta('name', 'robots', null);
    if (/^\/profile\/[^/]+$/.test(path)) { setCanonical(`${SITE_URL}${path}`); return; }
    setMeta('name', 'robots', 'noindex');
    setCanonical(null);
  }, [pathname]);

  return null;
};
